/**
 * ClientesPage Component
 * 
 * Página de listagem e gerenciamento de clientes.
 * Permite visualizar, criar, editar e excluir clientes.
 * 
 * Funcionalidades:
 * - Listagem de clientes com paginação
 * - Filtros de busca
 * - Modal de criação/edição
 * - Exclusão com confirmação
 * - Visualização de detalhes
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Edit, Trash2, Plus, Search, Upload } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import { customerService } from '../../../../services/erpApi'
import './ClientesPage.css'

interface Cliente {
  id: number
  name: string
  email?: string
  phone?: string
  cpf_cnpj?: string
  address_street?: string
  address_number?: string
  address_complement?: string
  address_neighborhood?: string
  address_city?: string
  address_state?: string
  address_zipcode?: string
  address_country?: string
  type?: string
  status?: string
  state_registration?: string
  state_registration_exempt?: boolean
}

interface FormData {
  name: string
  email: string
  phone: string
  cpfCnpj: string
  addressStreet: string
  addressNumber: string
  addressComplement: string
  addressNeighborhood: string
  addressCity: string
  addressState: string
  addressZipcode: string
  addressCountry: string
  type: string
  status: string
  stateRegistration: string
  stateRegistrationExempt: boolean
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

const ClientesPage: React.FC = () => {
  const navigate = useNavigate()
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingCliente, setEditingCliente] = useState<Cliente | null>(null)
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    cpfCnpj: '',
    addressStreet: '',
    addressNumber: '',
    addressComplement: '',
    addressNeighborhood: '',
    addressCity: '',
    addressState: '',
    addressZipcode: '',
    addressCountry: 'BR',
    type: 'F',
    status: 'active',
    stateRegistration: '',
    stateRegistrationExempt: false
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })

  const loadClientes = useCallback(async () => {
    setLoading(true)
    try {
      const response = await customerService.getAll({ 
        search: searchTerm,
        page: pagination.page,
        limit: pagination.limit
      })
      if (response.success) {
        setClientes(response.data || [])
        if (response.pagination) {
          setPagination({
            page: response.pagination.page,
            limit: response.pagination.limit,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          })
        }
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
    } finally {
      setLoading(false)
    }
  }, [searchTerm, pagination.page, pagination.limit])

  useEffect(() => {
    loadClientes()
  }, [loadClientes])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    try {
      // Preparar dados no formato esperado pelo backend
      const dataToSend = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        cpfCnpj: formData.cpfCnpj ? formData.cpfCnpj.replace(/\D/g, '') : null,
        addressStreet: formData.addressStreet || null,
        addressNumber: formData.addressNumber || null,
        addressComplement: formData.addressComplement || null,
        addressNeighborhood: formData.addressNeighborhood || null,
        addressCity: formData.addressCity || null,
        addressState: formData.addressState || null,
        addressZipcode: formData.addressZipcode ? formData.addressZipcode.replace(/\D/g, '') : null,
        addressCountry: formData.addressCountry || 'BR',
        type: formData.type || 'F',
        status: formData.status || 'active',
        stateRegistration: formData.stateRegistrationExempt
          ? null
          : (formData.stateRegistration ? formData.stateRegistration.replace(/\D/g, '') : null),
        stateRegistrationExempt: formData.type === 'J' ? formData.stateRegistrationExempt : false
      }

      let response
      if (editingCliente) {
        response = await customerService.update(editingCliente.id, dataToSend)
      } else {
        response = await customerService.create(dataToSend)
      }

      if (response.success) {
        setModalOpen(false)
        resetForm()
        loadClientes()
      } else {
        // Tratar erros de validação
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj: Record<string, string> = {}
          response.errors.forEach((err: any) => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar cliente' })
        }
      }
    } catch (error: any) {
      console.error('Erro ao salvar cliente:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar cliente'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj: Record<string, string> = {}
        errorDetails.forEach((err: any) => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }

  const handleEdit = (cliente: Cliente) => {
    navigate(`/erp/pessoas/clientes/${cliente.id}/editar`)
  }

  const handleDelete = async (cliente: Cliente) => {
    if (window.confirm(`Deseja realmente excluir o cliente ${cliente.name}?`)) {
      try {
        const response = await customerService.delete(cliente.id)
        if (response.success) {
          loadClientes()
        }
      } catch (error) {
        console.error('Erro ao excluir cliente:', error)
        alert('Erro ao excluir cliente')
      }
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      cpfCnpj: '',
      addressStreet: '',
      addressNumber: '',
      addressComplement: '',
      addressNeighborhood: '',
      addressCity: '',
      addressState: '',
      addressZipcode: '',
      addressCountry: 'BR',
      type: 'F',
      status: 'active',
      stateRegistration: '',
      stateRegistrationExempt: false
    })
    setEditingCliente(null)
    setErrors({})
  }

  const columns = [
    { key: 'name', label: 'Nome', sortable: true },
    { key: 'email', label: 'E-mail', sortable: true },
    { key: 'phone', label: 'Telefone' },
    { key: 'cpf_cnpj', label: 'CPF/CNPJ' },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => {
        const statusMap: Record<string, { label: string; class: string }> = {
          active: { label: 'Ativo', class: 'active' },
          inactive: { label: 'Inativo', class: 'inactive' },
          blocked: { label: 'Bloqueado', class: 'blocked' }
        }
        const status = statusMap[value] || statusMap.inactive
        return (
          <span className={`status-badge ${status.class}`}>
            {status.label}
          </span>
        )
      }
    }
  ]

  const actions = [
    {
      label: 'Editar',
      icon: Edit,
      variant: 'primary',
      onClick: handleEdit
    },
    {
      label: 'Excluir',
      icon: Trash2,
      variant: 'danger',
      onClick: handleDelete
    }
  ]

  return (
    <div className="clientes-page">
      <div className="page-header">
        <div>
          <h1>Clientes</h1>
          <p>Gerencie seus clientes</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            className="btn-secondary"
            onClick={() => navigate('/erp/pessoas/clientes/importar')}
          >
            <Upload size={18} />
            Importar
          </button>
          <button
            className="btn-primary"
            onClick={() => navigate('/erp/pessoas/clientes/novo')}
          >
            <Plus size={18} />
            Novo Cliente
          </button>
        </div>
      </div>

      <div className="page-filters">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Buscar clientes..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setPagination({ ...pagination, page: 1 })
            }}
            className="search-input"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={clientes}
        loading={loading}
        actions={actions}
        emptyMessage="Nenhum cliente encontrado"
        onRowClick={undefined}
        pagination={pagination.total > 0 ? {
          from: (pagination.page - 1) * pagination.limit + 1,
          to: Math.min(pagination.page * pagination.limit, pagination.total),
          total: pagination.total,
          current: pagination.page,
          totalPages: pagination.totalPages,
          hasPrev: pagination.page > 1,
          hasNext: pagination.page < pagination.totalPages,
          onPrev: () => setPagination({ ...pagination, page: pagination.page - 1 }),
          onNext: () => setPagination({ ...pagination, page: pagination.page + 1 })
        } : null}
      />

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title={editingCliente ? 'Editar Cliente' : 'Novo Cliente'}
        size="medium"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
            >
              {editingCliente ? 'Salvar' : 'Criar'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField
            label="Nome"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            placeholder=""
            mask=""
            required
          />

          <FormField
            label="E-mail"
            name="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={errors.email}
            placeholder=""
            mask=""
          />

          <FormField
            label="Telefone"
            name="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            error={errors.phone}
            placeholder=""
            mask="phone"
          />

          <FormField
            label="CPF/CNPJ"
            name="cpfCnpj"
            value={formData.cpfCnpj}
            onChange={(e) => {
              const value = e.target.value
              // Aplicar máscara baseada no tamanho
              const cleanValue = value.replace(/\D/g, '')
              const masked = cleanValue.length <= 11
                ? cleanValue.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2')
                : cleanValue.replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
              const nextType = cleanValue.length <= 11 ? 'F' : 'J'
              setFormData({
                ...formData,
                cpfCnpj: masked,
                type: nextType,
                stateRegistrationExempt:
                  nextType === 'J' ? (formData.stateRegistrationExempt || !formData.stateRegistration) : false
              })
            }}
            error={errors.cpfCnpj}
            placeholder="000.000.000-00 ou 00.000.000/0000-00"
            mask=""
          />

          <FormField
            label="Tipo"
            name="type"
            type="select"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            error=""
            placeholder=""
            mask=""
            options={[
              { value: 'F', label: 'Pessoa Física' },
              { value: 'J', label: 'Pessoa Jurídica' }
            ]}
          />

          {formData.type === 'J' && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <input
                  type="checkbox"
                  checked={formData.stateRegistrationExempt}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      stateRegistrationExempt: e.target.checked,
                      stateRegistration: e.target.checked ? '' : formData.stateRegistration
                    })
                  }
                />
                Isento de Inscrição Estadual
              </label>
              {!formData.stateRegistrationExempt && (
                <FormField
                  label="Inscrição Estadual (IE)"
                  name="stateRegistration"
                  value={formData.stateRegistration}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      stateRegistration: e.target.value.replace(/\D/g, '')
                    })
                  }
                  error={errors.stateRegistration}
                  placeholder="Somente números"
                  mask=""
                />
              )}
            </>
          )}

          <FormField
            label="Rua"
            name="addressStreet"
            value={formData.addressStreet}
            onChange={(e) => setFormData({ ...formData, addressStreet: e.target.value })}
            error={errors.addressStreet}
            placeholder=""
            mask=""
          />

          <div className="form-row">
            <FormField
              label="Número"
              name="addressNumber"
              value={formData.addressNumber}
              onChange={(e) => setFormData({ ...formData, addressNumber: e.target.value })}
              error={errors.addressNumber}
              placeholder=""
              mask=""
            />

            <FormField
              label="Complemento"
              name="addressComplement"
              value={formData.addressComplement}
              onChange={(e) => setFormData({ ...formData, addressComplement: e.target.value })}
              error={errors.addressComplement}
              placeholder=""
              mask=""
            />
          </div>

          <FormField
            label="Bairro"
            name="addressNeighborhood"
            value={formData.addressNeighborhood}
            onChange={(e) => setFormData({ ...formData, addressNeighborhood: e.target.value })}
            error={errors.addressNeighborhood}
            placeholder=""
            mask=""
          />

          <div className="form-row">
            <FormField
              label="Cidade"
              name="addressCity"
              value={formData.addressCity}
              onChange={(e) => setFormData({ ...formData, addressCity: e.target.value })}
              error={errors.addressCity}
              placeholder=""
              mask=""
            />

            <FormField
              label="Estado"
              name="addressState"
              value={formData.addressState}
              onChange={(e) => setFormData({ ...formData, addressState: e.target.value.toUpperCase() })}
              error={errors.addressState}
              placeholder=""
              mask=""
              maxLength={2}
            />

            <FormField
              label="CEP"
              name="addressZipcode"
              value={formData.addressZipcode}
              onChange={(e) => setFormData({ ...formData, addressZipcode: e.target.value })}
              error={errors.addressZipcode}
              placeholder=""
              mask="cep"
            />
          </div>

          <FormField
            label="Status"
            name="status"
            type="select"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            error=""
            placeholder=""
            mask=""
            options={[
              { value: 'active', label: 'Ativo' },
              { value: 'inactive', label: 'Inativo' },
              { value: 'blocked', label: 'Bloqueado' }
            ]}
          />

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>
    </div>
  )
}

export default ClientesPage
